type ListBlock =
  | { kind: "ordered"; start: number; count: number; marker?: string | null }
  | { kind: "unordered" | "break" };

/** Continue each run of sibling lists. Child lists have their own numbering. */
export function continuousListStarts(blocks: ListBlock[]): (number | null)[] {
  let next: number | null = null;
  let marker: string | null = null;
  return blocks.map((block) => {
    if (block.kind === "break") {
      next = null;
      marker = null;
    }
    if (block.kind !== "ordered") return null;
    const currentMarker = block.marker || "1";
    // Keep the first list's start, then continue across bullet/check lists too.
    // Recompute later starts so inserting/removing earlier items cannot leave
    // stale numbers behind. A paragraph/heading separates independent lists.
    const start = next !== null && marker === currentMarker ? next : block.start;
    next = start + block.count;
    marker = currentMarker;
    return start;
  });
}

type MarkdownNode = {
  type: string;
  ordered?: boolean;
  start?: number | null;
  children?: MarkdownNode[];
};

/** Apply the same sibling-list rules to read-only Markdown without changing its source. */
export function remarkContinuousLists() {
  return function visit(node: MarkdownNode) {
    if (!node.children) return;
    const starts = continuousListStarts(node.children.map((child) => child.type !== "list"
      ? { kind: "break" }
      : child.ordered
        ? { kind: "ordered", start: child.start ?? 1, count: child.children?.length ?? 0 }
        : { kind: "unordered" }));
    node.children.forEach((child, index) => {
      if (starts[index] !== null) child.start = starts[index];
      visit(child);
    });
  };
}
